import React, { FormEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import MarketingModal from './MarketingModal';
import { ArrowRight } from 'lucide-react';
import ModalFooter from './common/ModalFooter';
import PhoneInputWithCountry from './common/PhoneInputWithCountry';
import apiClient, { ApiError } from '../services/apiClient';

interface BookDemoModalProps {
    isOpen: boolean;
    onClose: () => void;
    source?: string | null;
}

interface DemoResponse {
    id: string;
    created_at: string;
}

interface FormState {
    name: string;
    email: string;
    company: string;
    phone: string;
    message: string;
}

const INITIAL_FORM: FormState = {
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
};

const MESSAGE_MAX = 2000;
const NAME_MAX = 255;
const EMAIL_MAX = 320;
const COMPANY_MAX = 255;
const PHONE_MAX = 64;

// Basic RFC-5322-ish check. Server does the real validation.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildPayload = (
    form: FormState,
    source?: string | null,
): Record<string, string> => {
    const body: Record<string, string> = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
    };
    const phone = form.phone.trim();
    if (phone) body.phone = phone;
    const message = form.message.trim();
    if (message) body.message = message;
    if (source) body.source = source;
    return body;
};

const validate = (
    form: FormState,
): { errors: Partial<Record<keyof FormState, string>>; ok: boolean } => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    const name = form.name.trim();
    if (!name) errors.name = 'Please enter your name.';
    else if (name.length > NAME_MAX)
        errors.name = `Keep it under ${NAME_MAX} characters.`;

    const email = form.email.trim();
    if (!email) errors.email = 'Please enter your work email.';
    else if (email.length > EMAIL_MAX)
        errors.email = `Keep it under ${EMAIL_MAX} characters.`;
    else if (!EMAIL_RE.test(email))
        errors.email = "That doesn't look like a valid email.";

    const company = form.company.trim();
    if (!company) errors.company = 'Please enter your company.';
    else if (company.length > COMPANY_MAX)
        errors.company = `Keep it under ${COMPANY_MAX} characters.`;

    if (form.phone.length > PHONE_MAX)
        errors.phone = `Phone is too long (max ${PHONE_MAX}).`;
    if (form.message.length > MESSAGE_MAX)
        errors.message = `Message is too long (max ${MESSAGE_MAX}).`;

    return { errors, ok: Object.keys(errors).length === 0 };
};

const mapServerError = (err: ApiError | Error): string => {
    if (err instanceof ApiError) {
        if (err.status === 422)
            return 'Please check your details and try again.';
        if (err.status === 429) {
            return "You've submitted a few requests recently — please try again later or email support@comtev.com directly.";
        }
    }
    return 'Something went wrong. Please email support@comtev.com or try again in a moment.';
};

const TickIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <polyline points="4 12 10 18 20 6" />
    </svg>
);

const BookDemoModal: React.FC<BookDemoModalProps> = ({
    isOpen,
    onClose,
    source,
}) => {
    const [form, setForm] = useState<FormState>(INITIAL_FORM);
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<keyof FormState, string>>
    >({});
    const [topError, setTopError] = useState<string | null>(null);
    const [success, setSuccess] = useState<DemoResponse | null>(null);

    const mutation = useMutation<DemoResponse, ApiError, void>({
        mutationFn: () =>
            apiClient<DemoResponse>('/demo/book', {
                method: 'POST',
                body: buildPayload(form, source),
                skipAuth: true,
                showLoading: false,
            }),
        onMutate: () => {
            setTopError(null);
        },
        onSuccess: (data) => {
            setSuccess(data);
        },
        onError: (err) => {
            setTopError(mapServerError(err));
        },
    });

    const resetAll = () => {
        setForm(INITIAL_FORM);
        setFieldErrors({});
        setTopError(null);
        setSuccess(null);
        mutation.reset();
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const updateField = <K extends keyof FormState>(
        key: K,
        value: FormState[K],
    ) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (fieldErrors[key]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (mutation.isPending) return;
        const { errors, ok } = validate(form);
        if (!ok) {
            setFieldErrors(errors);
            setTopError('Please complete the highlighted fields.');
            document.getElementById(`bd-${Object.keys(errors)[0]}`)?.focus();
            return;
        }
        setFieldErrors({});
        mutation.mutate();
    };

    const isPending = mutation.isPending;
    const referenceShort = success ? success.id.slice(0, 8) : null;

    const footer = success ? (
        <ModalFooter>
            <ModalFooter.Primary>
                <button
                    type="button"
                    className="lp3-btn-primary marketing-modal-primary"
                    onClick={handleClose}
                >
                    Close
                </button>
            </ModalFooter.Primary>
        </ModalFooter>
    ) : (
        <ModalFooter>
            <ModalFooter.Secondary>
                <button
                    type="button"
                    className="lp3-btn-secondary marketing-modal-secondary"
                    onClick={handleClose}
                    disabled={isPending}
                >
                    Cancel
                </button>
            </ModalFooter.Secondary>
            <ModalFooter.Primary>
                <button
                    type="submit"
                    form="book-demo-form"
                    className="lp3-btn-primary marketing-modal-primary"
                    disabled={isPending}
                >
                    {isPending ? 'Sending request...' : 'Request a demo'}
                    {!isPending && <ArrowRight size={16} aria-hidden="true" />}
                </button>
            </ModalFooter.Primary>
        </ModalFooter>
    );

    return (
        <MarketingModal
            isOpen={isOpen}
            onClose={handleClose}
            title={success ? 'Request received.' : 'Book a demo.'}
            footer={footer}
            maxWidth="820px"
            className="marketing-demo"
        >
            {success ? (
                <div className="book-demo-success" role="status">
                    <div className="book-demo-success-tick" aria-hidden="true">
                        <TickIcon />
                    </div>
                    <h3 className="book-demo-success-h3">
                        We&apos;ve got your request
                    </h3>
                    <p className="book-demo-success-body">
                        Someone from the Comtev team will be in touch within{' '}
                        <strong>2 working days</strong>. If you need us sooner,
                        email{' '}
                        <a
                            href="mailto:support@comtev.com"
                            className="book-demo-link"
                        >
                            support@comtev.com
                        </a>
                        .
                    </p>
                    {referenceShort && (
                        <div className="book-demo-reference">
                            Reference <span>{referenceShort}</span>
                        </div>
                    )}
                </div>
            ) : (
                <div className="marketing-demo-layout">
                    <aside className="marketing-demo-intro">
                        <span className="marketing-modal-kicker">
                            YOUR NEXT EVENT
                        </span>
                        <h3>Bring a real event.</h3>
                        <p>
                            Have a brief, a venue in mind, or just a date? We
                            will use it to walk you through Comtev.
                        </p>
                        <ol>
                            <li>
                                <span>01</span> Find venues and suppliers
                            </li>
                            <li>
                                <span>02</span> Review replies and quotes
                            </li>
                            <li>
                                <span>03</span> Build a budget for approval
                            </li>
                        </ol>
                        <span className="marketing-demo-note">
                            Tell us what you need to see.
                        </span>
                    </aside>
                    <form
                        id="book-demo-form"
                        className="book-demo-form"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        {topError && (
                            <div className="book-demo-banner" role="alert">
                                {topError}
                            </div>
                        )}

                        <div className="book-demo-field">
                            <label
                                htmlFor="bd-name"
                                className="book-demo-label"
                            >
                                Name{' '}
                                <span
                                    className="book-demo-required"
                                    aria-hidden="true"
                                >
                                    *
                                </span>
                            </label>
                            <input
                                id="bd-name"
                                type="text"
                                autoComplete="name"
                                className={`form-input${fieldErrors.name ? ' book-demo-input-invalid' : ''}`}
                                value={form.name}
                                onChange={(e) =>
                                    updateField('name', e.target.value)
                                }
                                maxLength={NAME_MAX}
                                required
                                aria-required="true"
                                aria-invalid={Boolean(fieldErrors.name)}
                                aria-describedby={
                                    fieldErrors.name ? 'bd-name-err' : undefined
                                }
                                disabled={isPending}
                            />
                            {fieldErrors.name && (
                                <div
                                    id="bd-name-err"
                                    className="book-demo-field-err"
                                >
                                    {fieldErrors.name}
                                </div>
                            )}
                        </div>

                        <div className="book-demo-field">
                            <label
                                htmlFor="bd-email"
                                className="book-demo-label"
                            >
                                Work email{' '}
                                <span
                                    className="book-demo-required"
                                    aria-hidden="true"
                                >
                                    *
                                </span>
                            </label>
                            <input
                                id="bd-email"
                                type="email"
                                autoComplete="email"
                                className={`form-input${fieldErrors.email ? ' book-demo-input-invalid' : ''}`}
                                value={form.email}
                                onChange={(e) =>
                                    updateField('email', e.target.value)
                                }
                                maxLength={EMAIL_MAX}
                                required
                                aria-required="true"
                                aria-invalid={Boolean(fieldErrors.email)}
                                aria-describedby={
                                    fieldErrors.email
                                        ? 'bd-email-err'
                                        : undefined
                                }
                                disabled={isPending}
                            />
                            {fieldErrors.email && (
                                <div
                                    id="bd-email-err"
                                    className="book-demo-field-err"
                                >
                                    {fieldErrors.email}
                                </div>
                            )}
                        </div>

                        <div className="book-demo-field book-demo-field--wide">
                            <label
                                htmlFor="bd-company"
                                className="book-demo-label"
                            >
                                Company{' '}
                                <span
                                    className="book-demo-required"
                                    aria-hidden="true"
                                >
                                    *
                                </span>
                            </label>
                            <input
                                id="bd-company"
                                type="text"
                                autoComplete="organization"
                                className={`form-input${fieldErrors.company ? ' book-demo-input-invalid' : ''}`}
                                value={form.company}
                                onChange={(e) =>
                                    updateField('company', e.target.value)
                                }
                                maxLength={COMPANY_MAX}
                                required
                                aria-required="true"
                                aria-invalid={Boolean(fieldErrors.company)}
                                aria-describedby={
                                    fieldErrors.company
                                        ? 'bd-company-err'
                                        : undefined
                                }
                                disabled={isPending}
                            />
                            {fieldErrors.company && (
                                <div
                                    id="bd-company-err"
                                    className="book-demo-field-err"
                                >
                                    {fieldErrors.company}
                                </div>
                            )}
                        </div>

                        <div className="book-demo-field">
                            <label
                                htmlFor="bd-phone"
                                className="book-demo-label"
                            >
                                Phone{' '}
                                <span className="book-demo-optional">
                                    optional
                                </span>
                            </label>
                            <PhoneInputWithCountry
                                id="bd-phone"
                                value={form.phone}
                                onChange={(value) =>
                                    updateField('phone', value)
                                }
                                placeholder="Include country code"
                                defaultCountry="GB"
                                marketing
                                disabled={isPending}
                            />
                            {fieldErrors.phone && (
                                <div className="book-demo-field-err">
                                    {fieldErrors.phone}
                                </div>
                            )}
                        </div>

                        <div className="book-demo-field book-demo-field--wide">
                            <label
                                htmlFor="bd-message"
                                className="book-demo-label"
                            >
                                What would you like to see?{' '}
                                <span className="book-demo-optional">
                                    optional
                                </span>
                            </label>
                            <textarea
                                id="bd-message"
                                className={`form-input book-demo-textarea${fieldErrors.message ? ' book-demo-input-invalid' : ''}`}
                                rows={3}
                                value={form.message}
                                onChange={(e) =>
                                    updateField(
                                        'message',
                                        e.target.value.slice(0, MESSAGE_MAX),
                                    )
                                }
                                maxLength={MESSAGE_MAX}
                                disabled={isPending}
                            />
                            <div className="book-demo-counter">
                                {form.message.length} / {MESSAGE_MAX}
                            </div>
                            {fieldErrors.message && (
                                <div className="book-demo-field-err">
                                    {fieldErrors.message}
                                </div>
                            )}
                        </div>
                        <p className="marketing-demo-privacy">
                            We use these details to arrange your demo.{' '}
                            <a href="/privacy">Privacy policy</a>
                        </p>
                    </form>
                </div>
            )}
        </MarketingModal>
    );
};

export default BookDemoModal;
